import { Alert, FormInput, Typography } from '@ecdlink/ui';
import { Header } from '@/pages/infant/infant-profile/components';
import NutritionCare from '@/assets/nutritionCare.svg';
import { DynamicFormProps } from '../../dynamic-form';
import { ChangeEvent, useEffect, useState } from 'react';
import { SuccessCard } from '@/components/success-card/success-card';
import { ReactComponent as CelebrateIcon } from '@/assets/celebrateIcon.svg';
import { replaceBraces } from '@ecdlink/core';

export const muacQuestion = `What is {client} mid-upper arm circumference (MUAC) today?`;

export const muacFormSection =
  'Mother growth monitoring (Mid-upper arm circumference)';

export const MotherGrowthMUACStep = ({
  mother,
  setEnableButton,
  setSectionQuestions,
}: DynamicFormProps) => {
  const [answer, setAnswer] = useState('');
  const [notUnderWeight, setNotUnderWeight] = useState(0);

  const muacRegex =
    /^(10(?:\.\d{1,3})?|1[1-9](?:\.\d{1,3})?|[2-4]\d(?:\.\d{1,3})?|50(?:\.0{1,3})?)$/; // min 10 max 50
  const errorMessage = !muacRegex.test(String(answer).replaceAll(',', '.'));

  const underWeightError =
    Number(String(answer).replaceAll(',', '.')) < 22 &&
    Number(String(answer).replaceAll(',', '.')) > 0;
  const notUnderWeightMessage = notUnderWeight > 21 && notUnderWeight < 100;

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setAnswer(value);
    setSectionQuestions?.([
      {
        visitSection: muacFormSection,
        questions: [
          {
            question: muacQuestion,
            answer: String(value).replaceAll(',', '.'),
          },
        ],
      },
    ]);
  };

  useEffect(() => {
    setEnableButton?.(true);
  }, [setEnableButton]);

  useEffect(() => {
    if (answer) {
      setNotUnderWeight(Number(String(answer).replaceAll(',', '.')));
    }
    const isValidMuac = muacRegex.test(String(answer).replaceAll(',', '.'));
    setEnableButton?.(isValidMuac);
  }, [answer, setEnableButton]);

  return (
    <>
      <Header
        backgroundColor="tertiary"
        customIcon={NutritionCare}
        title="MUAC measurement"
      />
      <div className="relative flex flex-col p-4">
        <Typography
          type="h4"
          color="textDark"
          text={replaceBraces(muacQuestion, mother?.user?.firstName!)}
        />
        <div className="mb-4 flex flex-row items-center gap-1">
          <FormInput
            placeholder={'Tap to add'}
            type={'text'}
            className="w-2/4"
            value={answer}
            min={0}
            maxLength={6}
            onChange={handleChange}
          ></FormInput>
          <Typography type="body" color="textDark" text="cm" className="mt-2" />
        </div>
        {answer && errorMessage && (
          <Typography
            type="body"
            color="errorMain"
            className="mb-4"
            text="Please check your measurement. You must enter a number between 10 and 50."
          />
        )}
        {underWeightError && (
          <Alert
            className="mb-4"
            type="error"
            title={`${mother?.user?.firstName} might be underweight. Refer her to the clinic.`}
            list={['Encourage her to eat 3 meals and 1 small snack every day.']}
          />
        )}
        {notUnderWeightMessage && (
          <SuccessCard
            customIcon={<CelebrateIcon className="h-14	w-14" />}
            text={`Great, ${mother?.user?.firstName} is not underweight!`}
            textColour="successDark"
            subTextColours="textDark"
            color="successBg"
          />
        )}
      </div>
    </>
  );
};
